import { gettext } from '@eventbrite/i18n';
import { Button } from '@eventbrite/marmalade';
import React, { useState } from 'react';
import { useEventBasicInformationContext } from '../../../../contexts';
import { useCondensedCheckout } from '../../../../experimentation/useCondensedCheckout';
import type {
    CheckoutWidgetProps,
    Event,
    Organizer,
    RemindMeProps,
} from '../../../../types';
import { shouldShowForDraftInPreview } from '../../../../utils';
import RemindMe from '../../../RemindMe/RemindMe';
import { RSVPLabel, RSVPOptions } from '../../../RSVP/RSVP';
import { CheckoutModalButton } from '../CheckoutModalButton/CheckoutModalButton';
import { ExploreSimilarEventsButton } from '../ExploreSimilarEventsButton/ExploreSimilarEventsButton';
import { CondensedConversionBar } from './CondensedConversionBar/CondensedConversionBar';
import './ConversionBar.scss';
import './ConversionBarTeExperiment.scss';
import { ScrollToDateButton } from './ScrollToDateButton';
import { ShowMoreButton, ShowMoreContent } from './ShowMore/ShowMore';

export type ConversionBarCommonProps = {
    statusIsUnavailable: boolean;
    shouldDisplayRsvp: boolean;
    shouldShowStatus: boolean;
    panelDisplayPrice: string;
    statusToDisplay: string;
    shouldShowCheckoutButton: boolean;
    ticketModalButton: any;
    shouldShowRemindMeButton: boolean;
    shouldShowTurnOffReminderButton: boolean;
    isExpiredEvent: boolean;
    salesStatus: string;
    isSalesEnded: boolean;
    isSoldOut: boolean;
    hasDonationTicketsAvailable: boolean;
    hasWaitlist: boolean;
    hasAvailableHiddenTickets: boolean;
    waitlistEnabled?: boolean;
    waitlistAvailable?: boolean;
    joinWaitlist?: boolean;
    enforceDateAndTimeVisualization?: boolean;
    onlyDefaultCheckoutForOnlineEvents?: boolean;
    useAllInPrice?: boolean;
    isOnlineEventMvpEnabled?: boolean;
    isTESessionSelectorExperimentEnabled?: boolean;
    panelDisplayMessage?: string;
    isFree: boolean;
};

export type ConversionBarComponentProps = ConversionBarCommonProps &
    CheckoutWidgetProps &
    RemindMeProps & {
        externalTickets?: {
            //TODO: build on the backend
            url: string;
            text: string;
            available: boolean;
            shouldShowExternalTicketingProvider: boolean;
            externalTicketingProviderLabelText?: string;
        };
    } & Pick<Event, 'isPreview' | 'childEvents' | 'isLive'> & {
        eventName?: Event['name'];
        organizerName?: Organizer['name'];
        organizerAvatar?: Organizer['profilePicture'];
    };

export const ConversionBar = ({
    affiliateCode,
    campaign_token,
    promoCode,
    rsvpToken,
    waitlistToken,
    statusIsUnavailable,
    shouldDisplayRsvp,
    shouldShowStatus,
    statusToDisplay,
    panelDisplayPrice,
    externalTickets,
    ticketModalButton,
    isPreview,
    isLive,
    shouldShowRemindMeButton,
    shouldShowCheckoutButton,
    eventName,
    isRemindedEvent,
    organizerId,
    organizerName,
    organizerAvatar,
    isAuthenticated,
    isSalesEnded,
    isSoldOut,
    hasDonationTicketsAvailable,
    hasAvailableHiddenTickets,
    hasWaitlist,
    waitlistAvailable,
    waitlistEnabled,
    enforceDateAndTimeVisualization = false,
    onlyDefaultCheckoutForOnlineEvents = false,
    useAllInPrice = false,
    isOnlineEventMvpEnabled = false,
    isTESessionSelectorExperimentEnabled = false,
    panelDisplayMessage = '',
    isFree = false,
}: ConversionBarComponentProps) => {
    const {
        shouldShowExternalTicketingProvider,
        externalTicketingProviderLabelText,
    } = externalTickets ?? {};
    const {
        isSeriesParent,
        start,
        place: { isOnline },
        locale,
        isEnded,
    } = useEventBasicInformationContext();

    const isCondensedCheckoutExperimentEnabled = useCondensedCheckout();

    const [showMoreOpen, setShowMoreOpen] = useState(false);

    const ExternalTickets = () => (
        <Button variant="primary" size="block">
            <a
                href={externalTickets?.url}
                rel="noreferrer nofollow"
                target="_blank"
                style={{ textDecoration: 'none', color: 'inherit' }}
            >
                {externalTickets?.text}
            </a>
        </Button>
    );

    const showExploreSimilarEventsButton =
        isEnded ||
        (shouldShowForDraftInPreview(isPreview, isLive) &&
            !hasAvailableHiddenTickets &&
            !hasDonationTicketsAvailable &&
            !hasWaitlist &&
            (isSalesEnded || isSoldOut));

    const showExtraPriceInformation =
        onlyDefaultCheckoutForOnlineEvents &&
        isOnlineEventMvpEnabled &&
        useAllInPrice;
    const extraPriceInformation = gettext(
        '+sales tax calculated at checkout',
    ).toString();

    const shouldShowCTA =
        !statusIsUnavailable &&
        (shouldShowCheckoutButton ||
            externalTickets?.available ||
            shouldDisplayRsvp);

    const CheckoutButton: React.FC = ({ children }) =>
        showExploreSimilarEventsButton ? (
            <ExploreSimilarEventsButton />
        ) : (
            <CheckoutModalButton
                affiliateCode={affiliateCode}
                campaign_token={campaign_token}
                isPreview={isPreview}
                promoCode={promoCode}
                rsvpToken={rsvpToken}
                shouldShowStatus={shouldShowStatus}
                ticketModalButton={ticketModalButton}
                waitlistToken={waitlistToken}
                joinWaitlist={
                    hasWaitlist && (waitlistAvailable || waitlistEnabled)
                }
                isFree={isFree}
                isRemindMe={shouldShowRemindMeButton}
            >
                {children}
            </CheckoutModalButton>
        );

    const CondensedCheckoutButton = () => {
        const displayText = shouldShowStatus
            ? statusToDisplay
            : panelDisplayPrice;
        return (
            <CondensedConversionBar
                startDateTime={start}
                displayText={displayText}
                checkoutButton={shouldShowCTA ? <CheckoutButton /> : null}
                isOnline={isOnline}
                locale={locale}
                externalTickets={externalTickets}
                extraPriceInformation={
                    showExtraPriceInformation ? extraPriceInformation : ''
                }
            />
        );
    };

    if (isCondensedCheckoutExperimentEnabled && !shouldShowRemindMeButton) {
        return <CondensedCheckoutButton />;
    }

    const CheckoutArea = () => (
        <>
            {shouldShowRemindMeButton && (
                <RemindMe
                    eventName={eventName}
                    isRemindedEvent={isRemindedEvent}
                    organizerId={organizerId}
                    organizerName={organizerName}
                    organizerAvatar={organizerAvatar}
                    isAuthenticated={isAuthenticated}
                />
            )}
            {enforceDateAndTimeVisualization ? (
                <ScrollToDateButton />
            ) : (
                <CheckoutButton />
            )}
        </>
    );

    const SingleEvent = () => (
        <>
            {shouldDisplayRsvp ? (
                <RSVPOptions
                    {...{
                        affiliateCode,
                        campaign_token,
                        isPreview,
                        promoCode,
                        rsvpToken,
                        shouldShowStatus,
                        ticketModalButton,
                        waitlistToken,
                        isFree,
                    }}
                />
            ) : (
                <>
                    {shouldShowCheckoutButton && <CheckoutArea />}
                    {externalTickets?.available && <ExternalTickets />}
                </>
            )}
        </>
    );

    const SeriesParent = () => {
        return enforceDateAndTimeVisualization ? (
            <ScrollToDateButton />
        ) : (
            <CheckoutButton />
        );
    };

    const PanelInfo = () => {
        const status = shouldShowStatus ? statusToDisplay : panelDisplayPrice;

        return (
            <div
                className={
                    isTESessionSelectorExperimentEnabled
                        ? 'conversion-bar-te__panel-info'
                        : 'conversion-bar__panel-info'
                }
                data-testid="panel-info"
            >
                {shouldDisplayRsvp ? (
                    <div id="listings-root__rsvp-label">
                        <RSVPLabel />
                    </div>
                ) : (
                    <>
                        {status}
                        {panelDisplayMessage &&
                        isTESessionSelectorExperimentEnabled ? (
                            <div className="conversion-bar-te__panel-message">
                                {panelDisplayMessage}
                            </div>
                        ) : null}
                        {shouldShowExternalTicketingProvider && (
                            <>
                                {' '}
                                <span className="conversion-bar__additional-info">
                                    {externalTicketingProviderLabelText}
                                </span>
                            </>
                        )}
                        {showExtraPriceInformation && (
                            <>
                                <ShowMoreButton
                                    onToggle={() =>
                                        setShowMoreOpen(!showMoreOpen)
                                    }
                                />
                                <ShowMoreContent
                                    isOpen={showMoreOpen}
                                    content={extraPriceInformation}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        );
    };

    return (
        <div
            className={
                isTESessionSelectorExperimentEnabled
                    ? 'conversion-bar conversion-bar-te__checkout-opener'
                    : 'conversion-bar conversion-bar--checkout-opener'
            }
            data-testid="conversion-bar"
        >
            <div
                className={
                    isTESessionSelectorExperimentEnabled
                        ? 'conversion-bar-te__body'
                        : 'conversion-bar__body'
                }
            >
                <PanelInfo />
            </div>
            {shouldShowCTA && (
                <div
                    className={
                        isTESessionSelectorExperimentEnabled
                            ? 'conversion-bar-te__cta-wrapper'
                            : 'conversion-bar__cta-wrapper'
                    }
                >
                    {isSeriesParent ? <SeriesParent /> : <SingleEvent />}
                </div>
            )}
        </div>
    );
};
